import { knobMenuIndex } from './knobUniform';
import type { ShaderUniform } from './shaderParser';

/**
 * What the knob panel shows, shared by the DOM panel (VirtualKnobs) and the
 * sleepy-ui one (src/sleepy-ui/knobsModel), so the two can never disagree.
 */

/** Layering (back to front): Code (105) < Knobs (108) < Library (110). */
export const KNOBS_PANEL_Z_INDEX = 108;

/** The mobile / desktop header heights the panel sits below (Layout.tsx). */
export const KNOBS_HEADER_HEIGHT = { mobile: 60, desktop: 68 } as const;

/**
 * The mobile (K)NOBS tab's top, below the panel's own top: lower on a
 * profile, at 72px over the feed or fullscreen, else just below the header
 * plus the media texture bar when it shows.
 */
export const KNOBS_MOBILE_TAB_TOP = { profile: 150, feedOrFullscreen: 72, header: 65 } as const;

/** The CSS variable the media texture bar publishes its height in. */
export const MEDIA_BAR_OFFSET_VAR = '--media-bar-offset';

/** A knob's label: the uniform's own, else its name without the u_ prefix. */
export const knobLabel = (uniform: ShaderUniform): string => uniform.label || uniform.name.replace('u_', '');

/**
 * ISF `long` inputs are menus: the label of the entry the knob's 0-1 value
 * selects — the same entry whose value the uniform gets (knobUniform.ts) —
 * or undefined for any other input or an entry without a label.
 */
export const knobMenuLabel = (uniform: ShaderUniform, value: number): string | undefined => {
  if (!uniform.labels?.length) return undefined;
  const index = knobMenuIndex(uniform, value);
  return index >= 0 ? uniform.labels[index] : undefined;
};

/** Where the mobile tab sits, as a CSS `top`. */
export const knobsMobileTabTopCss = (profileOpen: boolean, fullscreenOrFeed: boolean): string =>
  profileOpen
    ? `${KNOBS_MOBILE_TAB_TOP.profile}px`
    : fullscreenOrFeed
      ? `${KNOBS_MOBILE_TAB_TOP.feedOrFullscreen}px`
      : `calc(${KNOBS_MOBILE_TAB_TOP.header}px + var(${MEDIA_BAR_OFFSET_VAR}, 0px))`;

/** The same in px, given the media bar's current offset. */
export const knobsMobileTabTopPx = (profileOpen: boolean, fullscreenOrFeed: boolean, mediaBarOffset: number): number =>
  profileOpen
    ? KNOBS_MOBILE_TAB_TOP.profile
    : fullscreenOrFeed
      ? KNOBS_MOBILE_TAB_TOP.feedOrFullscreen
      : KNOBS_MOBILE_TAB_TOP.header + (Number.isFinite(mediaBarOffset) ? mediaBarOffset : 0);

/** The desktop header's MIDI error label (shown uppercase), or '' for none. */
export const midiErrorLabel = (midi: { error?: string | null; isSupported: boolean }): string =>
  !midi.error ? '' : midi.error.includes('NotAllowedError') ? 'No Permission' : !midi.isSupported ? 'Not Supported' : 'Error';
